import React, { useEffect, useRef } from 'react';
import { useLocation, useOutlet } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';

const MAX_CACHE_SIZE = 20;

const routeCache = new Map<string, React.ReactElement>();
let accessOrder: string[] = [];

/**
 * Gộp các tab UserDetail (/users/:id/m-photo-pro|lite|mono|…) thành 1 cache key
 * để đổi Pro ↔ Lite ↔ Mono không remount.
 * Trừ trang chi tiết frame: /users/:id/custom-frames/:frameId
 */
function getKeepAliveCacheKey(pathname: string, search: string): string {
  if (/^\/users\/[^/]+\/(?:m-photo-lite\/)?custom-frames\/[^/]+/.test(pathname)) {
    return `${pathname}${search}`;
  }
  const userMatch = pathname.match(/^\/users\/([^/]+)/);
  if (userMatch) {
    return `/users/${userMatch[1]}`;
  }
  return `${pathname}${search}`;
}

/** Xóa cache khi đăng xuất (gọi từ auth hoặc effect bên dưới). */
export function clearKeepAliveCache(): void {
  routeCache.clear();
  accessOrder = [];
}

type CachedPageProps = {
  active: boolean;
  children: React.ReactNode;
};

/** Giữ trang trong DOM — hiện/ẩn tức thì, không fade (tránh khựng khi chuyển mục). */
const CachedPageShell: React.FC<CachedPageProps> = ({ active, children }) => (
  <div
    style={{ display: active ? 'block' : 'none' }}
    aria-hidden={!active}
  >
    {children}
  </div>
);

/**
 * Giữ các trang đã mở trong DOM (ẩn bằng display:none) thay vì unmount.
 * Khi user bấm Back, trang cũ vẫn giữ scroll, filter, data đã load.
 */
const KeepAliveOutlet: React.FC = () => {
  const location = useLocation();
  const outlet = useOutlet();
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const prevAuthRef = useRef(isAuthenticated);

  const cacheKey = getKeepAliveCacheKey(location.pathname, location.search);

  useEffect(() => {
    if (prevAuthRef.current && !isAuthenticated) {
      clearKeepAliveCache();
    }
    prevAuthRef.current = isAuthenticated;
  }, [isAuthenticated]);

  // Trang theme luôn render mới — không giữ bản cũ.
  if (
    location.pathname.startsWith('/themes') ||
    location.pathname.includes('/themes/')
  ) {
    return <React.Fragment key={location.pathname}>{outlet}</React.Fragment>;
  }

  if (outlet && !routeCache.has(cacheKey)) {
    if (accessOrder.length >= MAX_CACHE_SIZE) {
      const oldest = accessOrder.shift();
      if (oldest) routeCache.delete(oldest);
    }
    routeCache.set(cacheKey, outlet);
    accessOrder.push(cacheKey);
  } else if (outlet && routeCache.has(cacheKey)) {
    accessOrder = accessOrder.filter((k) => k !== cacheKey);
    accessOrder.push(cacheKey);
  }

  return (
    <>
      {accessOrder.map((key) => (
        <CachedPageShell key={key} active={key === cacheKey}>
          {routeCache.get(key)}
        </CachedPageShell>
      ))}
    </>
  );
};

export default KeepAliveOutlet;
